import { Divider, Stack, SxProps, Theme, useTheme } from "@mui/material";
import type { Editor } from "@tiptap/react";
import {
  BlockQuotesIcon,
  BoldIcon,
  BulletPointsIcon,
  ItalicIcon,
  NumberListIcon,
  StrikethroughIcon,
  TextV2Icon,
  UnderlineIcon,
} from "components";
import { HighlightPopover } from "components/editor/extensions/text-highlight";
import { Popover } from "components/popover";
import { memo, useCallback, useEffect, useReducer, useRef } from "react";

// ─── Types ───────────────────────────────────────────────────────────

interface ChatFormattingToolbarProps {
  editor: Editor | null;
  containerSx?: SxProps<Theme>;
}

interface ToolbarButton {
  icon: React.ElementType;
  command: () => void;
  isActive: boolean;
  label: string;
}

// ─── Component ───────────────────────────────────────────────────────

export const ChatFormattingToolbar = memo(
  ({ editor, containerSx }: ChatFormattingToolbarProps) => {
    const theme = useTheme();
    const [, forceRender] = useReducer((x: number) => x + 1, 0);

    useEffect(() => {
      if (!editor) return;
      editor.on("transaction", forceRender);
      return () => {
        editor.off("transaction", forceRender);
      };
    }, [editor]);

    const savedSelectionRef = useRef<{ from: number; to: number } | null>(null);

    const saveSelection = useCallback(() => {
      if (!editor) return;
      const { from, to } = editor.state.selection;
      savedSelectionRef.current = { from, to };
    }, [editor]);

    if (!editor) return null;

    const activeTextColor = editor.getAttributes("textStyle").color || null;
    const activeHighlightColor =
      editor.getAttributes("highlight").color || null;

    const buttonGroups: ToolbarButton[][] = [
      // Text formatting
      [
        {
          icon: BoldIcon,
          command: () => editor.chain().focus().toggleBold().run(),
          isActive: editor.isActive("bold"),
          label: "Bold",
        },
        {
          icon: ItalicIcon,
          command: () => editor.chain().focus().toggleItalic().run(),
          isActive: editor.isActive("italic"),
          label: "Italic",
        },
        {
          icon: UnderlineIcon,
          command: () => editor.chain().focus().toggleUnderline().run(),
          isActive: editor.isActive("underline"),
          label: "Underline",
        },
        {
          icon: StrikethroughIcon,
          command: () => editor.chain().focus().toggleStrike().run(),
          isActive: editor.isActive("strike"),
          label: "Strikethrough",
        },
      ],
      // Lists
      [
        {
          icon: BulletPointsIcon,
          command: () => editor.chain().focus().toggleBulletList().run(),
          isActive: editor.isActive("bulletList"),
          label: "Bullet list",
        },
        {
          icon: NumberListIcon,
          command: () => editor.chain().focus().toggleOrderedList().run(),
          isActive: editor.isActive("orderedList"),
          label: "Numbered list",
        },
        {
          icon: BlockQuotesIcon,
          command: () => editor.chain().focus().toggleBlockquote().run(),
          isActive: editor.isActive("blockquote"),
          label: "Blockquote",
        },
      ],
    ];

    return (
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          py: "8px",
          px: "16px",
          gap: "4px",
          ...containerSx,
        }}
        divider={<Divider orientation="vertical" flexItem />}
      >
        {buttonGroups.map((group, groupIndex) => (
          <Stack
            key={groupIndex}
            direction="row"
            sx={{
              alignItems: "center",
              gap: "4px",
            }}
          >
            {group.map((btn) => {
              const Icon = btn.icon;
              return (
                <Stack
                  key={btn.label}
                  component="button"
                  direction="row"
                  aria-label={btn.label}
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    btn.command();
                  }}
                  sx={{
                    cursor: "pointer",
                    minWidth: "32px",
                    minHeight: "32px",
                    borderRadius: "6px",
                    padding: "4px",
                    outline: "none",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "none",
                    gap: "8px",
                    backgroundColor: btn.isActive
                      ? theme.palette.neutral["005"]
                      : "transparent",
                    "&:hover": {
                      backgroundColor: theme.palette.neutral["010"],
                    },
                  }}
                >
                  <Icon
                    sx={{
                      width: 20,
                      height: 20,
                      color: theme.palette.lightBg.medium,
                    }}
                  />
                </Stack>
              );
            })}
          </Stack>
        ))}

        {/* Text color & highlight */}
        <Popover
          paperSx={{ mb: "8px" }}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          transformOrigin={{ vertical: "bottom", horizontal: "center" }}
          popoverContent={(closePopover) => {
            // Create a proxy editor that restores selection before commands
            const editorWithSelection = {
              ...editor,
              chain: () => {
                const sel = savedSelectionRef.current;
                if (sel) {
                  editor
                    .chain()
                    .focus()
                    .setTextSelection({ from: sel.from, to: sel.to })
                    .run();
                }
                return editor.chain();
              },
            } as typeof editor;
            return (
              <HighlightPopover
                editor={editorWithSelection}
                onClose={closePopover}
              />
            );
          }}
        >
          <Stack
            component="button"
            direction="row"
            aria-label="Text color"
            onClick={saveSelection}
            sx={{
              cursor: "pointer",
              minWidth: "32px",
              minHeight: "32px",
              borderRadius: "6px",
              padding: "4px",
              outline: "none",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
              backgroundColor: activeHighlightColor || "transparent",
              "&:hover": {
                backgroundColor:
                  activeHighlightColor || theme.palette.neutral["010"],
              },
            }}
          >
            <TextV2Icon
              sx={{
                width: 20,
                height: 20,
                color: activeTextColor || theme.palette.lightBg.medium,
              }}
            />
          </Stack>
        </Popover>
      </Stack>
    );
  },
);
